import {
  areaLevelAtom,
  firstAreaAtom,
  mapRefAtom,
  markersAtom,
  secondAreaAtom,
  showHeatmapAtom,
  userLocationMarkerAtom,
} from "@/api/atoms";
import { layersQuery } from "@/atoms/layersQueries";
import type { MarkerType } from "@/types";
import { createGeoJSONCircle } from "@/utils";

import "mapbox-gl/dist/mapbox-gl.css";

import { heatmapQuery } from "@/api/queries";
import { AnimatePresence } from "framer-motion";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { Layer, type MapRef, Source } from "react-map-gl";

import { MapSource } from "./MapSource";
import { Marker } from "./Marker";
import MarkersList from "./MarkerList";
import { Popup } from "./Popup";
import {
  boroughsLayer,
  circleLayer,
  citiesLayer,
  countriesLayer,
  districtsLayer,
  heatmapLayer,
  radiusLayer1,
  radiusLayer2,
  userRadiusLayer,
} from "./map-style";

const emptyCollection = {
  type: "FeatureCollection",
  features: [],
};

const defaultGeojson = {
  country: emptyCollection,
  county: emptyCollection,
  city: emptyCollection,
  borough: emptyCollection,
};

const MapContent = () => {
  const [layers] = useAtom(layersQuery);
  const [heatmap] = useAtom(heatmapQuery);

  const geojson = layers?.data ?? defaultGeojson;

  const countries = geojson.country;
  const districts = geojson.county;
  const cities = geojson.city;
  const boroughs = geojson.borough;

  const [firstArea, setFirstArea] = useAtom(firstAreaAtom);
  const [secondArea, setSecondArea] = useAtom(secondAreaAtom);
  const areaLevel = useAtomValue(areaLevelAtom);
  const showHeatmap = useAtomValue(showHeatmapAtom);

  const [markers, setMarkers] = useAtom(markersAtom);
  const [userMarker, _setUserMarker] = useAtom(userLocationMarkerAtom);

  const [previousLevel, setPreviousLevel] = useState("country");

  const mapRef = useRef<MapRef | null>(null);

  const [map, setMapRef] = useAtom(mapRefAtom);

  if (mapRef != null && !map) {
    setMapRef(mapRef.current);
  }

  const source =
    areaLevel === "country"
      ? "world-countries"
      : areaLevel === "county"
        ? "eng-districts"
        : areaLevel === "city"
          ? "eng-cities"
          : "eng-boroughs";

  useEffect(() => {
    const isSearch = markers.filter((el) => el?.isSearch).length > 0;
    if (areaLevel !== previousLevel && !isSearch) {
      setPreviousLevel(areaLevel);
      setMarkers([]);
    }
  }, [areaLevel, setMarkers]);

  return (
    <>
      <MapSource id="world-countries" data={countries} layers={[countriesLayer]} />
      <MapSource id="eng-districts" data={districts} layers={[districtsLayer]} />
      <MapSource id="eng-cities" data={cities} layers={[citiesLayer]} />
      <MapSource id="eng-boroughs" data={boroughs} layers={[boroughsLayer]} />
      <Source
        id="marker-1"
        type="geojson"
        data={{
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [-5.331378, 54.052001],
              },
              properties: {},
            },
          ],
        }}
      >
        <Layer {...radiusLayer1} />
      </Source>
      {markers
        .filter((marker) => marker?.isSearch)
        .map((marker, i) => (
          <Source
            key={`search-marker-${i + 1}`}
            id={`search-marker-${i + 1}`}
            type="geojson"
            data={createGeoJSONCircle([marker.lng, marker.lat], 2 * 0.621371)}
          >
            <Layer {...(i === 0 ? radiusLayer1 : radiusLayer2)} />
          </Source>
        ))}
      {userMarker && (
        <Source
          id="user-marker"
          type="geojson"
          data={createGeoJSONCircle([userMarker.longitude, userMarker.latitude], 2 * 0.621371)}
        >
          <Layer {...userRadiusLayer} />
        </Source>
      )}
      {showHeatmap && (
        <MapSource id="heatmap" data={heatmap.data} layers={[heatmapLayer, circleLayer]} />
      )}
      <AnimatePresence>
        <MarkersList
          markers={markers}
          showDetails={!secondArea}
          removeMarker={(marker: MarkerType) => {
            setMarkers(markers.filter((el) => el.location !== marker.location));
            const isFirstArea = marker.location === firstArea?.name;

            if (isFirstArea) {
              if (secondArea) {
                map?.setFeatureState(
                  { source, id: firstArea?.id },
                  {
                    "selected-1": false,
                  },
                );
                map?.setFeatureState(
                  { source, id: secondArea?.id },
                  {
                    "selected-1": true,
                  },
                );
                map?.setFeatureState(
                  { source, id: secondArea?.id },
                  {
                    "selected-2": false,
                  },
                );
                setFirstArea(secondArea);
                setSecondArea(null);
              } else {
                map?.setFeatureState(
                  { source, id: firstArea?.id },
                  {
                    "selected-1": false,
                  },
                );
                setFirstArea(null);
              }
            } else {
              map?.setFeatureState(
                { source, id: secondArea?.id },
                {
                  "selected-2": false,
                },
              );
              setSecondArea(null);
            }
          }}
        />
      </AnimatePresence>
      {userMarker && (
        <>
          <Marker type="user" lat={userMarker.latitude} lng={userMarker.longitude} />
          <Popup
            counts={userMarker.counts}
            lat={userMarker.latitude}
            lng={userMarker.longitude}
            location="Your Location"
            details={"In a 2 mile radius of this location"}
          />
        </>
      )}
    </>
  );
};

export default MapContent;
